import React, { useEffect, useMemo } from "react";
import BaseModal from "@reusables/Modals/BaseModal";
import BaseButton from "@reusables/BaseButton";
import { useTranslation } from "react-i18next";
import BaseNotFound from "@reusables/BaseNotFound";
import { useFieldArray, useForm } from "react-hook-form";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import { zodResolver } from "@hookform/resolvers/zod";
import { BatchGroup } from "./BatchGroup";
import { stopPropagate } from "@helpers/utils";
import { DatepickerDayjs } from "@reusables/BaseDatepicker/types";
import { BatchNumbersScheme, formScheme } from "./types";
import { useBatchNumberOptions } from "./hooks";

/**
 * This modal handles the validation of batch numbers.
 * Users can attach these numbers (click the "attach" button) if they meet the following criteria:
 * 1. All input fields are filled.
 * 2. For batch numbers, the number of inputs should not exceed the selected quantity. This applies to every product line.
 *
 * Once batch numbers are attached, users have the option to modify them or use them during the creation of a picking record.
 * If used in the picking creation, there might be instances where the number of batches are not filled (at least 1) for a product line.
 * In such cases:
 * 1. Users can edit and add more batch numbers.
 * 2. An error message will be displayed, prompting the user to add at least one batch number to each product line.
 *
 * P.S. This modal operates with dates in dayjs format, however the original batch_numbers are stored as strings in the parent RHF.
 */
interface BatchNumbersModalProps {
    isOpen: boolean;
    onClose: () => void;

    /**
     * Note: only FILTERED lines should be provided. Component doesn't handle any picked_quantity or outgoing_location checks.
     */
    lines: {
        id: number;
        comment?: string;
        product: {
            id: number;
            name: string;
            code: string;
        };
        picked_quantity?: number;
        batch_numbers?: {
            batch_number: string;
            expiration_date?: DatepickerDayjs;
        }[];
    }[];

    onSave?: (data: BatchNumbersScheme) => void;
    preview?: boolean;
}

export type BatchNumberAutocompleteOption = {
    batch_number: string;
    expiration_date?: DatepickerDayjs;
};

export default function BatchNumbersModal({
                                              isOpen,
                                              onClose,
                                              lines: propLines,
                                              onSave,
                                              preview = false
                                          }: BatchNumbersModalProps) {
    const { t } = useTranslation("", { keyPrefix: "sales.picking.modals.batchNumbers" });

    const { control, handleSubmit, setValue, reset } = useForm<BatchNumbersScheme>({
        resolver: zodResolver(formScheme)
    });

    const { fields: lines } = useFieldArray({
        control,
        name: "lines"
    });

    // Extract product IDs for batch number options fetching
    const productIds = useMemo(() =>
            lines.reduce((acc, line) => {
                if (line.product.id && !acc.includes(line.product.id)) {
                    acc.push(line.product.id);
                }
                return acc;
            }, [] as number[]),
        [lines]);

    // Custom hook to manage batch number options
    const { batchNumbersOptions, isLoading } = useBatchNumberOptions(productIds);

    // Handle form submission
    const onSubmit = handleSubmit(data => onSave?.(data));

    // Initialize form when modal opens
    useEffect(() => {
        if (isOpen) {
            const formReadyLines = propLines.map(line => ({
                id: line.id,
                comment: line.comment,
                product: {
                    id: line.product.id,
                    name: line.product.name,
                    code: line.product.code
                },
                picked_quantity: Math.ceil(line.picked_quantity ?? 0),
                batch_numbers: line.batch_numbers?.length
                    ? line.batch_numbers
                    : [{ batch_number: "" }]
            }));

            setValue("lines", formReadyLines);
        } else {
            reset();
        }
    }, [propLines, isOpen, setValue, reset]);

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            width={900}
            padding="56px"
            position="center"
            isLoading={isLoading}
        >
            <form onSubmit={stopPropagate(onSubmit)} className="space-y-[32px]">
                {lines.length ? (
                    lines.map((line, lineIndex) => (
                        <BatchGroup
                            key={line.id}
                            index={lineIndex}
                            control={control}
                            preview={preview}
                            batchNumbersOptions={batchNumbersOptions[line.product.id]}
                        />
                    ))
                ) : (
                    <BaseNotFound text={t("noProductsPicked")} height={250} />
                )}

                <BaseInputsGrid cols={preview ? 1 : 2}>
                    <BaseButton
                        text={t("buttons.cancel")}
                        size="md"
                        buttonWidth="100%"
                        primaryOutlined
                        type="button"
                        onClick={onClose}
                    />

                    {!preview && (
                        <BaseButton
                            text={t("buttons.attach")}
                            size="md"
                            buttonWidth="100%"
                        />
                    )}
                </BaseInputsGrid>
            </form>
        </BaseModal>
    );
}
